import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { LogIn, Menu, X } from 'lucide-react';
import UserAvatar from '@/components/UserAvatar';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSiteSettings } from '@/hooks/useSiteSettings';

const NavigationHeader = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userType, setUserType] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [profileId, setProfileId] = useState<string>('');
  const { currentUser } = useCurrentUser();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // التحقق من حالة المستخدم الحالية
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      if (user) {
        // الحصول على نوع المستخدم من البيانات الوصفية
        const type = user.user_metadata?.user_type || 'tenant';
        setUserType(type);

        // Get profile ID for notifications
        if (type === 'landlord') {
          const { data: landlordData } = await supabase
            .from('landlords')
            .select('id')
            .eq('user_id', user.id)
            .single();
          if (landlordData) setProfileId(landlordData.id);
        } else if (type === 'tenant') {
          const { data: tenantData } = await supabase
            .from('tenants')
            .select('id')
            .eq('user_id', user.id)
            .single();
          if (tenantData) setProfileId(tenantData.id);
        }
      }
    };

    getUser();

    // الاستماع لتغييرات حالة المصادقة
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          const type = session.user.user_metadata?.user_type || 'tenant';
          setUserType(type);
        } else {
          setUserType(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsMenuOpen(false);
    navigate('/');
  };

  const handleDashboardClick = () => {
    setIsMenuOpen(false);
    if (userType === 'landlord') {
      navigate('/owner-dashboard');
    } else {
      navigate('/user-dashboard');
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-50 animate-fade-in">
      <div className="container mx-auto px-4 py-3"> {/* تم تعديل py-4 إلى py-3 لاستيعاب الشعار الأكبر */}
        <div className="flex items-center justify-between">
          {/* الشعار المعدل */}
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="bg-white rounded-full p-1 shadow-md border border-gray-200">
              <img 
                src={settings.logo_url} 
                alt={`شعار ${settings.site_title}`} 
                className="h-12 w-12 rounded-full object-cover" // تم التعديل هنا
              />
            </div>
            <span className="text-xl font-bold text-gray-800 hidden sm:block">
              {settings.site_title}
            </span>
          </Link>

          {/* روابط التنقل الرئيسية - سطح المكتب */}
          <nav className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className={`text-gray-700 hover:text-blue-600 transition-colors font-medium ${
                isActive('/') ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : ''
              }`}
            >
              الرئيسية
            </Link>
            <Link 
              to="/properties" 
              className={`text-gray-700 hover:text-blue-600 transition-colors font-medium ${
                isActive('/properties') ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : ''
              }`}
            >
              العقارات
            </Link>
            <Link 
              to="/services" 
              className={`text-gray-700 hover:text-blue-600 transition-colors font-medium ${
                isActive('/services') ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : ''
              }`}
            >
              الخدمات
            </Link>
            <Link 
              to="/maintenance" 
              className={`text-gray-700 hover:text-blue-600 transition-colors font-medium ${
                isActive('/maintenance') ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : ''
              }`}
            >
              الصيانة
            </Link>
            <Link 
              to="/property-management-request" 
              className={`text-gray-700 hover:text-blue-600 transition-colors font-medium ${
                isActive('/property-management-request') ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : ''
              }`}
            >
              طلب إدارة العقار
            </Link>
            <Link 
              to="/about" 
              className={`text-gray-700 hover:text-blue-600 transition-colors font-medium ${
                isActive('/about') ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : ''
              }`}
            >
              {settings.about_title}
            </Link>
            {user && userType === 'tenant' && (
              <Link 
                to="/user-dashboard" 
                className={`text-gray-700 hover:text-blue-600 transition-colors font-medium ${
                  isActive('/user-dashboard') ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : ''
                }`}
              >
                ملفي الشخصي
              </Link>
            )}
          </nav>

          {/* أزرار المستخدم - سطح المكتب */}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <div className="flex items-center gap-2">
                {profileId && userType && (userType === 'landlord' || userType === 'tenant') && (
                  <NotificationCenter userType={userType as 'landlord' | 'tenant'} userId={profileId} />
                )}
                <UserAvatar />
              </div>
            ) : (
              <>
                <Button variant="outline" asChild>
                  <Link to="/login">
                    <LogIn className="h-4 w-4 mr-2" />
                    تسجيل الدخول
                  </Link>
                </Button>
                <Button asChild>
                  <Link to="/register">
                    إنشاء حساب
                  </Link>
                </Button>
              </>
            )}
          </div>

          {/* زر القائمة المنسدلة - الهواتف المحمولة */}
          <div className="md:hidden flex items-center gap-2">
            {user && profileId && userType && (userType === 'landlord' || userType === 'tenant') && (
              <NotificationCenter userType={userType as 'landlord' | 'tenant'} userId={profileId} />
            )}
            <button
              className="p-2 text-gray-700 hover:text-blue-600"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* القائمة المنسدلة للهواتف المحمولة */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t pt-4">
            <div className="flex flex-col gap-4">
              {/* روابط التنقل */}
              <Link 
                to="/" 
                className={`text-gray-700 hover:text-blue-600 transition-colors font-medium py-2 ${
                  isActive('/') ? 'text-blue-600' : ''
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                الرئيسية
              </Link>
               <Link 
                 to="/properties" 
                 className={`text-gray-700 hover:text-blue-600 transition-colors font-medium py-2 ${
                   isActive('/properties') ? 'text-blue-600' : ''
                 }`}
                 onClick={() => setIsMenuOpen(false)}
               >
                 العقارات
               </Link>
               <Link 
                 to="/services" 
                 className={`text-gray-700 hover:text-blue-600 transition-colors font-medium py-2 ${
                   isActive('/services') ? 'text-blue-600' : ''
                 }`}
                 onClick={() => setIsMenuOpen(false)}
               >
                 الخدمات
               </Link>
               <Link 
                 to="/maintenance" 
                 className={`text-gray-700 hover:text-blue-600 transition-colors font-medium py-2 ${
                   isActive('/maintenance') ? 'text-blue-600' : ''
                 }`}
                 onClick={() => setIsMenuOpen(false)}
               >
                 الصيانة
               </Link>
               <Link 
                 to="/property-management-request" 
                 className={`text-gray-700 hover:text-blue-600 transition-colors font-medium py-2 ${
                   isActive('/property-management-request') ? 'text-blue-600' : ''
                 }`}
                 onClick={() => setIsMenuOpen(false)}
               >
                 طلب إدارة العقار
               </Link>
               <Link 
                 to="/about" 
                 className={`text-gray-700 hover:text-blue-600 transition-colors font-medium py-2 ${
                   isActive('/about') ? 'text-blue-600' : ''
                 }`}
                 onClick={() => setIsMenuOpen(false)}
               >
                 {settings.about_title}
               </Link>
              
              {/* أزرار المستخدم */}
              <div className="border-t pt-4 flex flex-col gap-2">
                {user ? (
                  <>
                    <Button variant="outline" onClick={handleDashboardClick} className="w-full justify-start">
                      لوحة التحكم
                    </Button>
                    <Button variant="outline" onClick={handleLogout} className="w-full justify-start">
                      تسجيل الخروج
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" asChild className="w-full justify-start">
                      <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                        <LogIn className="h-4 w-4 mr-2" />
                        تسجيل الدخول
                      </Link>
                    </Button>
                    <Button asChild className="w-full justify-start">
                      <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                        إنشاء حساب
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default NavigationHeader;